import { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Robot,
  FloppyDisk,
  CalendarCheck,
  MessengerLogo,
  InstagramLogo,
  Check,
  Sparkle
} from '@phosphor-icons/react';

function WhatsAppIcon({ size = 18, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Phone, Tag, PlusCircle, Trash, ChatText } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BotConfig() {
  const { t } = useLanguage();
  const { loading: authLoading } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openingHours, setOpeningHours] = useState(null);
  const [ohLoading, setOhLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return; // Wait until token is restored from storage
    fetchConfig();
    fetchOpeningHours();
  }, [authLoading]);
  
  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/bot-config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching bot config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOpeningHours = async () => {
    try {
      const response = await axios.get(`${API}/opening-hours`);
      setOpeningHours(response.data);
    } catch (error) {
      console.error('Error fetching opening hours:', error);
    } finally {
      setOhLoading(false);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    
    try {
      await axios.put(`${API}/bot-config`, {
        system_prompt: config.system_prompt,
        model_name: config.model_name,
        enable_gecko_booking: config.enable_gecko_booking,
        active_platforms: config.active_platforms,
        language: config.language,
        contact_phone: config.contact_phone,
        welcome_message: config.welcome_message,
        locations_kw: config.locations_kw,
        treatments_kw: config.treatments_kw,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Error saving bot config:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const togglePlatform = (platform) => {
    const current = config.active_platforms || [];
    const updated = current.includes(platform)
      ? current.filter(p => p !== platform)
      : [...current, platform];
    setConfig({ ...config, active_platforms: updated });
  };

  // Keyword dict helpers
  const updateKw = (field, oldKey, newKey, newVal) => {
    const kw = { ...(config[field] || {}) };
    if (oldKey !== newKey) delete kw[oldKey];
    kw[newKey] = newVal;
    setConfig({ ...config, [field]: kw });
  };
  const deleteKw = (field, key) => {
    const kw = { ...(config[field] || {}) };
    delete kw[key];
    setConfig({ ...config, [field]: kw });
  };
  const addKw = (field) => {
    const kw = { ...(config[field] || {}), '': '' };
    setConfig({ ...config, [field]: kw });
  };

  const KwEditor = ({ field, label }) => {
    const entries = Object.entries(config?.[field] || {});
    return (
      <div className="space-y-2">
        {entries.map(([k, v], i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input
              value={k}
              placeholder="søgeord"
              className="font-mono text-sm w-40 flex-shrink-0"
              onChange={(e) => updateKw(field, k, e.target.value, v)}
            />
            <span className="text-zinc-400">→</span>
            <Input
              value={v}
              placeholder="visningsnavn"
              className="font-mono text-sm flex-1"
              onChange={(e) => updateKw(field, k, k, e.target.value)}
            />
            <button onClick={() => deleteKw(field, k)} className="text-zinc-400 hover:text-red-500 transition-colors p-1">
              <Trash size={15} />
            </button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => addKw(field)} className="mt-2 text-xs">
          <PlusCircle size={14} className="mr-1" /> Tilføj
        </Button>
        <p className="text-xs text-zinc-400 mt-1">Søgeord (hvad kunden skriver) → Visningsnavn (internt navn i systemet)</p>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className="p-8 max-w-4xl" data-testid="bot-config-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold text-zinc-900 tracking-tight">
          {t('botConfiguration')}
        </h1>
        <p className="text-zinc-500 mt-1">
          Configure your AI bot powered by Claude
        </p>
      </div>
      
      <div className="space-y-6">
        {/* Model Selection */}
        <Card className="bg-white border-zinc-200">
          <CardHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Robot size={20} weight="duotone" className="text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-lg">{t('model')}</CardTitle>
                <CardDescription>Vælg hvilken Claude-model der svarer kunder</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { id: 'claude-haiku-4-5-20251001', vendor: 'Claude', label: 'Haiku', desc: 'Hurtigst og billigst', color: 'emerald' },
                { id: 'claude-sonnet-4-6', vendor: 'Claude', label: 'Sonnet', desc: 'Bedste balance', color: 'blue' },
                { id: 'claude-opus-4-6', vendor: 'Claude', label: 'Opus', desc: 'Mest intelligent', color: 'indigo' },
                { id: 'gpt-4o', vendor: 'OpenAI', label: 'GPT-4o', desc: 'OpenAI · hurtig & alsidig', color: 'teal' },
              ].map(({ id, vendor, label, desc, color }) => {
                const active = (config?.model_name || 'claude-opus-4-6') === id;
                return (
                  <button
                    key={id}
                    onClick={() => setConfig({ ...config, model_name: id })}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                      active
                        ? `border-${color}-500 bg-${color}-50`
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Sparkle size={24} weight="fill" className={active ? `text-${color}-500` : 'text-zinc-300'} />
                      {active && (
                        <div className={`w-5 h-5 rounded-full bg-${color}-500 flex items-center justify-center`}>
                          <Check size={12} weight="bold" className="text-white" />
                        </div>
                      )}
                    </div>
                    <h4 className="font-semibold text-zinc-900">{vendor === 'Claude' ? `Claude ${label}` : label}</h4>
                    <p className="text-xs text-zinc-500 mt-0.5">{desc}</p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        {/* System Prompt */}
        <Card className="bg-white border-zinc-200">
          <CardHeader>
            <CardTitle className="text-lg">{t('systemPrompt')}</CardTitle>
            <CardDescription>{t('systemPromptDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={config?.system_prompt || ''}
              onChange={(e) => setConfig({ ...config, system_prompt: e.target.value })}
              placeholder="Enter system prompt..."
              className="min-h-[500px] font-mono text-sm"
              data-testid="system-prompt-input"
            />
            <p className="text-xs text-zinc-400 mt-2">
              Indeholder alle Pollys regler og vejledning. Under samtaler tilføjer systemet automatisk kundeinfo, allerede indsamlede bookingoplysninger og kalenderdata.
            </p>
          </CardContent>
        </Card>
        
        {/* GECKO Booking */}
        <Card className="bg-white border-zinc-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CalendarCheck size={20} weight="duotone" className="text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">{t('enableGeckoBooking')}</CardTitle>
                  <CardDescription>{t('enableGeckoBookingDesc')}</CardDescription>
                </div>
              </div>
              <Switch
                checked={config?.enable_gecko_booking || false}
                onCheckedChange={(checked) => setConfig({ ...config, enable_gecko_booking: checked })}
                data-testid="gecko-booking-toggle"
              />
            </div>
          </CardHeader>
        </Card>
        
        {/* Contact Phone */}
        <Card className="bg-white border-zinc-200">
          <CardHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Phone size={20} weight="duotone" className="text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Kontakttelefon</CardTitle>
                <CardDescription>Vises til kunder ved aflysning og ingen ledige tider</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Input
              value={config?.contact_phone || ''}
              onChange={(e) => setConfig({ ...config, contact_phone: e.target.value })}
              placeholder="70 22 02 32"
              className="max-w-xs font-mono"
            />
          </CardContent>
        </Card>

        {/* Welcome Message */}
        <Card className="bg-white border-zinc-200">
          <CardHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <ChatText size={20} weight="duotone" className="text-teal-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-zinc-800">Velkomstbesked</CardTitle>
                <p className="text-xs text-zinc-500 mt-0.5">Vises til nye brugere i webchat</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={config?.welcome_message || ''}
              onChange={(e) => setConfig({ ...config, welcome_message: e.target.value })}
              placeholder="Hej! 👋 Jeg er Polly..."
              rows={5}
              className="font-mono text-sm"
            />
          </CardContent>
        </Card>

        {/* Keyword Mappings + Location Map */}
        <Card className="bg-white border-zinc-200">
          <CardHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Tag size={20} weight="duotone" className="text-violet-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Ord & steder</CardTitle>
                <CardDescription>Søgeord Polly bruger til at genkende behandlinger, byer og lokationer</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="w-full">
              <AccordionItem value="treatments">
                <AccordionTrigger className="text-sm font-medium">
                  Behandlinger — {Object.keys(config?.treatments_kw || {}).length} søgeord
                </AccordionTrigger>
                <AccordionContent>
                  <KwEditor field="treatments_kw" label="Behandlinger" />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="locations_kw">
                <AccordionTrigger className="text-sm font-medium">
                  Byer (søgeord) — {Object.keys(config?.locations_kw || {}).length} søgeord
                </AccordionTrigger>
                <AccordionContent>
                  <KwEditor field="locations_kw" label="Byer" />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="location_map">
                <AccordionTrigger className="text-sm font-medium flex items-center gap-2">
                  <span>GECKO lokationer</span>
                  <Badge variant="secondary" className="text-xs ml-2">Skrivebeskyttet</Badge>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-xs text-zinc-500 mb-3">Disse ID'er brugt til at oprette bookinger i det rigtige GECKO-system. Ændres i kode (gecko_service.py).</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-zinc-500 border-b">
                          <th className="pb-2 pr-4 font-medium">By</th>
                          <th className="pb-2 pr-4 font-medium">customerGroupId</th>
                          <th className="pb-2 font-medium">calendarGroupId</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { name: 'København', cg: 52, cal: 1001 },
                          { name: 'Odense',    cg: 57, cal: 1002 },
                          { name: 'Kolding',   cg: 56, cal: 1003 },
                          { name: 'Aarhus',    cg: 58, cal: 1004 },
                          { name: 'Aalborg',   cg: 59, cal: 1005 },
                        ].map(row => (
                          <tr key={row.name} className="border-b border-zinc-100">
                            <td className="py-2 pr-4 font-medium">{row.name}</td>
                            <td className="py-2 pr-4 font-mono text-zinc-600">{row.cg}</td>
                            <td className="py-2 font-mono text-zinc-600">{row.cal}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Opening Hours */}
        {(() => {
          const DAYS = [
            { key: 'mon', label: 'Man' },
            { key: 'tue', label: 'Tir' },
            { key: 'wed', label: 'Ons' },
            { key: 'thu', label: 'Tor' },
            { key: 'fri', label: 'Fre' },
            { key: 'sat', label: 'Lør' },
            { key: 'sun', label: 'Søn' },
          ];
          const CITIES = ['København', 'Aarhus', 'Aalborg', 'Kolding', 'Odense'];
          const hours = openingHours?.hours || {};
          const addresses = openingHours?.addresses || {};
          const cachedAt = openingHours?.cached_at
            ? new Date(openingHours.cached_at).toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' })
            : null;
          return (
            <Card className="bg-white border-zinc-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-teal-600" style={{color:'#0d9488'}}>
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-lg">Åbningstider</CardTitle>
                      <CardDescription>
                        Scraped fra <a href="https://beautyandstyle.dk/afdelinger/" target="_blank" rel="noreferrer" className="underline hover:text-zinc-700">beautyandstyle.dk/afdelinger/</a>
                        {cachedAt && <span className="ml-2 text-zinc-400">· opdateret {cachedAt}</span>}
                      </CardDescription>
                    </div>
                  </div>
                  <button
                    onClick={fetchOpeningHours}
                    className="text-xs text-zinc-500 hover:text-zinc-800 border border-zinc-200 rounded px-2 py-1 transition-colors"
                  >
                    ↻ Genindlæs
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {ohLoading ? (
                  <div className="flex items-center gap-2 text-sm text-zinc-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-teal-500 border-t-transparent" />
                    Henter åbningstider…
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-zinc-500 border-b">
                            <th className="pb-2 pr-4 font-medium w-28">Afdeling</th>
                            <th className="pb-2 pr-4 font-medium w-48">Adresse</th>
                            {DAYS.map(d => (
                              <th key={d.key} className="pb-2 px-1 font-medium text-center w-14">{d.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {CITIES.map(city => {
                            const cityHours = hours[city] || {};
                            const cityAddr = addresses[city] || '—';
                            return (
                              <tr key={city} className="border-b border-zinc-100">
                                <td className="py-2 pr-4 font-medium text-zinc-800">{city}</td>
                                <td className="py-2 pr-4 text-xs text-zinc-500 whitespace-nowrap">{cityAddr}</td>
                                {DAYS.map(d => {
                                  const h = cityHours[d.key];
                                  // Format is "09:00-19:00" — split on the dash between times, not inside them
                                  const timePart = h ? h.replace(/\s*\(.*\)/, '').trim() : '';
                                  const dashIdx = timePart.lastIndexOf('-');
                                  const open  = dashIdx > 0 ? timePart.slice(0, dashIdx) : timePart;
                                  const close = dashIdx > 0 ? timePart.slice(dashIdx + 1) : '';
                                  return (
                                    <td key={d.key} className="py-2 px-1 text-center">
                                      {h ? (
                                        <span title={h} className="inline-flex flex-col items-center gap-0.5">
                                          <span className="bg-teal-50 text-teal-700 text-xs rounded px-1 py-0.5 font-mono leading-tight whitespace-nowrap">{open?.trim()}</span>
                                          <span className="bg-teal-50 text-teal-700 text-xs rounded px-1 py-0.5 font-mono leading-tight whitespace-nowrap">{close?.trim()}</span>
                                        </span>
                                      ) : (
                                        <span style={{background:'#fff1f2',color:'#f87171'}} className="inline-block text-xs rounded px-1.5 py-0.5 font-medium">Luk</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-zinc-400 mt-3">
                      Tiderne vises til Polly som kontekst — Gecko er altid afgørende for om der faktisk kan bookes.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Active Platforms */}
        <Card className="bg-white border-zinc-200">
          <CardHeader>
            <CardTitle className="text-lg">{t('activePlatformsLabel')}</CardTitle>
            <CardDescription>Select which platforms the bot should respond to</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {/* Messenger */}
              <button
                onClick={() => togglePlatform('messenger')}
                data-testid="platform-messenger-toggle"
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  config?.active_platforms?.includes('messenger')
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-zinc-200 hover:border-zinc-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <MessengerLogo 
                    size={32} 
                    weight="fill" 
                    className={config?.active_platforms?.includes('messenger') ? 'text-blue-600' : 'text-zinc-400'}
                  />
                  {config?.active_platforms?.includes('messenger') && (
                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                      <Check size={14} weight="bold" className="text-white" />
                    </div>
                  )}
                </div>
                <h4 className="font-medium text-zinc-900">Messenger</h4>
                <p className="text-sm text-zinc-500 mt-1">Facebook Messenger integration</p>
              </button>
              
              {/* Instagram */}
              <button
                onClick={() => togglePlatform('instagram')}
                data-testid="platform-instagram-toggle"
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  config?.active_platforms?.includes('instagram')
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-zinc-200 hover:border-zinc-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <InstagramLogo 
                    size={32} 
                    weight="fill" 
                    className={config?.active_platforms?.includes('instagram') ? 'text-purple-600' : 'text-zinc-400'}
                  />
                  {config?.active_platforms?.includes('instagram') && (
                    <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center">
                      <Check size={14} weight="bold" className="text-white" />
                    </div>
                  )}
                </div>
                <h4 className="font-medium text-zinc-900">Instagram</h4>
                <p className="text-sm text-zinc-500 mt-1">Instagram Direct Messages</p>
              </button>

              {/* WhatsApp */}
              <button
                onClick={() => togglePlatform('whatsapp')}
                data-testid="platform-whatsapp-toggle"
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  config?.active_platforms?.includes('whatsapp')
                    ? 'border-green-500 bg-green-50'
                    : 'border-zinc-200 hover:border-zinc-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <WhatsAppIcon
                    size={32}
                    className={config?.active_platforms?.includes('whatsapp') ? 'text-[#25D366]' : 'text-zinc-400'}
                  />
                  {config?.active_platforms?.includes('whatsapp') && (
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <Check size={14} weight="bold" className="text-white" />
                    </div>
                  )}
                </div>
                <h4 className="font-medium text-zinc-900">WhatsApp</h4>
                <p className="text-sm text-zinc-500 mt-1">WhatsApp Business integration</p>
              </button>

              {/* Webchat */}
              <button
                onClick={() => togglePlatform('webchat')}
                data-testid="platform-webchat-toggle"
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  config?.active_platforms?.includes('webchat')
                    ? 'border-sky-500 bg-sky-50'
                    : 'border-zinc-200 hover:border-zinc-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M16 3C8.82 3 3 8.37 3 15c0 3.56 1.61 6.76 4.18 8.97L6 29l5.45-2.65A13.7 13.7 0 0 0 16 27c7.18 0 13-5.37 13-12S23.18 3 16 3z"
                      fill={config?.active_platforms?.includes('webchat') ? '#0ea5e9' : '#d1d5db'}/>
                    <circle cx="10.5" cy="15" r="1.8" fill="white"/>
                    <circle cx="16" cy="15" r="1.8" fill="white"/>
                    <circle cx="21.5" cy="15" r="1.8" fill="white"/>
                  </svg>
                  {config?.active_platforms?.includes('webchat') && (
                    <div className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center">
                      <Check size={14} weight="bold" className="text-white" />
                    </div>
                  )}
                </div>
                <h4 className="font-medium text-zinc-900">Webchat</h4>
                <p className="text-sm text-zinc-500 mt-1">Chat-widget på beautyandstyle.dk</p>
              </button>
            </div>
          </CardContent>
        </Card>
        
        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-bot-config"
            className={`min-w-[140px] ${
              saved 
                ? 'bg-emerald-600 hover:bg-emerald-700' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                {t('saving')}
              </>
            ) : saved ? (
              <>
                <Check size={18} weight="bold" className="mr-2" />
                {t('saved')}
              </>
            ) : (
              <>
                <FloppyDisk size={18} weight="fill" className="mr-2" />
                {t('saveChanges')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
